import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import {Divider, Grid, Typography} from '@mui/material';

import {Column} from '../Column/Column';

import {JobPostingStatus} from './JobPostingStatus';
import {LanguageContext} from '../../contexts/LanguageContext/LanguageContext';
import {IconHeader, JobDetailsIcon} from '../../features/applicant/matches/components/IconHeader/IconHeader';
import {DepartmentDisplay} from '../../features/company/recruiting/job-postings/components/DepartmentsDisplay';
import {JobPosting} from '../../features/company/recruiting/job-postings/types/JobPostingsTypes';
import {orangeColors} from '../../layouts/theme/designSystem';
import {transformDate} from '../../utils/dateUtils/dateFormattingUtils';
import * as utils from '../../utils/jobPostingUtils/jobPostingUtils';

interface Props {
  jobPosting: JobPosting;
}
export const JobPostingHeader = ({jobPosting}: Props) => {
  const currentLanguage = useContext(LanguageContext);
  const {t} = useTranslation();
  const locationsAndDateColorBody1 = orangeColors[100];
  const iconColor = orangeColors[70];

  return (
    <Grid>
      <Grid item xs={12}>
        <Typography
          variant='h2'
          gutterBottom
          sx={{
            wordWrap: 'break-word',
            textAlign: {sm: 'left'},
          }}
        >
          {jobPosting?.title}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        sm={12}
        md={9}
        sx={{
          display: {xs: 'block', md: 'flex'},
          paddingBottom: '16px',
        }}
      >
        <Grid
          item
          xs={12}
          md={10}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <Typography variant='h4'>{jobPosting?.company}</Typography>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginLeft: {xs: '0px', md: '10px'},
          }}
        >
          <Typography variant='body1' color={locationsAndDateColorBody1}>
            {utils.formatLocations(jobPosting, currentLanguage)}
          </Typography>
        </Grid>
        <Grid display={'flex'} alignItems={'center'} gap={1}>
          <Grid
            item
            xs={12}
            md={8}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <Typography sx={{marginLeft: {md: '10px', xs: '0px'}}} variant='body1' color={locationsAndDateColorBody1}>
              {transformDate(jobPosting?.updatedAt || '', 'long')}
            </Typography>
            <JobPostingStatus jobPosting={jobPosting} color={locationsAndDateColorBody1} />
          </Grid>
        </Grid>
      </Grid>
      <Grid
        container
        display='flex'
        justifyContent='space-between'
        sx={{
          flexDirection: {xs: 'column', md: 'row'},
        }}
      >
        <Column md={4}>
          <DepartmentDisplay departments={jobPosting.departments || []} />
        </Column>
        <Column md={4}>
          <IconHeader
            iconColor={iconColor}
            icon={JobDetailsIcon.RemoteWork}
            textColor={orangeColors[40]}
            text={utils.formatWorkMode(jobPosting, t)}
          />
          <IconHeader
            iconColor={iconColor}
            icon={JobDetailsIcon.Knowledge}
            text={utils.formatExperienceAndLeaderShip(
              jobPosting,
              t,
              jobPosting?.experience?.yearsOfExperience ? jobPosting?.experience?.yearsOfExperience > 1 : false,
              jobPosting?.experience?.yearsOfLeadership ? jobPosting?.experience?.yearsOfLeadership > 1 : false
            )}
          />
          <IconHeader
            iconColor={iconColor}
            icon={JobDetailsIcon.BuildingColumns}
            text={utils.formatDegree(jobPosting, t)}
          />
        </Column>
        <Column md={4}>
          <IconHeader
            iconColor={iconColor}
            icon={JobDetailsIcon.Comment}
            text={utils.formatLanguages(jobPosting, currentLanguage)}
          />
          <IconHeader
            iconColor={iconColor}
            icon={JobDetailsIcon.Clock}
            text={utils.formatEmploymentType(jobPosting, t)}
          />
        </Column>
      </Grid>
      <Divider
        variant='fullWidth'
        sx={{
          padding: '14px',
          borderColor: orangeColors[3],
        }}
      />
    </Grid>
  );
};
